import React, { Fragment } from 'react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Link from 'react-router/lib/Link';
import { injectIntl } from 'react-intl';
import CSSModules from 'react-css-modules';

import { ReportInfo } from '../ReportInfoRow';
import { ReportPreview } from '../ReportPreview';
import Avatar from '../../../Avatar';
import Loader from '../../../common/Loader';
import Button from '../../../Button';
import { TimeShow } from '../../../common/TimeShow';
import ReportMenu from './ReportMenu';

import { COMPLAINT_TYPE } from '../../../../constants';
import { IDS } from '../../../../constants/ids';
import {
  COMMUNITIES,
  PUBLIC_PROFILE,
  POST,
} from '../../../../constants/routes';
import {
  COMPLAINT_RESOLVE_ACTIONS,
  COMPLAINT_RESOLVED_TITLES as TITLES,
} from '../../../../constants/props';

import styles from './styles.styl';

const ReportDetails = ({
  report: {
    id,
    realm,
    reportable,
    reason,
    createdAt,
    type,
    status,
    reporter,
    appliedActions,
  } = {},
  onResolve,
  loading,
  intl: { formatMessage },
}) => {
  const hasAppliedActions = Boolean(appliedActions);
  const menuProps = {
    id,
    formatMessage,
    onResolve,
    type,
    appliedActions: appliedActions || [],
  };

  const getSubtitle = () => {
    if (type === COMPLAINT_TYPE.POST || type === COMPLAINT_TYPE.COMMENT) {
      return (
        <Fragment>
          <Link
            styleName="link"
            to={`/${PUBLIC_PROFILE}/${get(reporter, 'id')}`}
          >
            {get(reporter, 'username')}
          </Link>
          &nbsp;reported&nbsp;
          <Link
            styleName="link"
            to={`/${PUBLIC_PROFILE}/${get(reportable, 'author.user.id')}`}
          >
            {get(reportable, 'author.user.aliasName')}
          </Link>
          's&nbsp; {type}
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Link styleName="link" to={`/${PUBLIC_PROFILE}/${get(reporter, 'id')}`}>
          {get(reporter, 'username')}
        </Link>
        <Link to={`/${COMMUNITIES}/${get(realm, 'id')}`}>
          &nbsp;{formatMessage({ id: 'community.reports.reported' })}
        </Link>
      </Fragment>
    );
  };

  const getHistory = () => {
    return appliedActions.map(action => {
      const isPost = type === COMPLAINT_TYPE.POST;
      const actionName = get(action, 'actionName');
      const isDestroyReportable =
        actionName === COMPLAINT_RESOLVE_ACTIONS.DESTROY_REPORTABLE;

      return (
        <li styleName="history-item" key={action.appliedAt}>
          <div>
            <If condition={isDestroyReportable}>
              <p styleName="history-item__text">
                {formatMessage({
                  id: isPost ? 'reports.post.type' : 'reports.comment.type',
                })}
                &nbsp;
              </p>
            </If>
            {formatMessage({ id: TITLES[actionName] })}
          </div>
          <TimeShow timestamp={action.appliedAt} hideIcon />
        </li>
      );
    });
  };

  const handleResolve = () => {
    const data = {
      id,
      mark_as_resolved: 'yes',
      ...(!hasAppliedActions && { apply_action: 'resolve_with_no_actions' }),
    };
    return onResolve(data, true);
  };

  if (loading) {
    return <Loader style={{ minWidth: 460 }} />;
  }

  const getPreviewLink = () => {
    const param = type === COMPLAINT_TYPE.COMMENT ? 'postId' : 'id';
    const postId = get(reportable, param);
    const communityId = get(realm, 'id');
    return `/${COMMUNITIES}/${communityId}/${POST}/${postId}`;
  };

  return (
    <div styleName="root">
      <div styleName="card">
        <Avatar
          src={get(realm, 'logo.thumbMin')}
          children={get(realm, 'name')}
          size={60}
          bordered
          position="center"
        />
        <div styleName="title">
          <span styleName="title__text">{get(realm, 'name')}</span>
          <span styleName="title__text-secondary">{getSubtitle()}</span>
        </div>
      </div>
      <div styleName="info">
        <ReportInfo
          formatMessage={formatMessage}
          type={type}
          status={status}
          reason={reason}
          createdAt={createdAt}
          column
          showTimerTitle
        />
      </div>
      <If condition={type !== COMPLAINT_TYPE.COMMUNITY}>
        <ReportPreview {...reportable} type={type} path={getPreviewLink()} />
      </If>
      <If condition={status !== 'resolved'}>
        <ReportMenu {...menuProps} />
        <div className="ml2 mt2">
          <Button
            id={IDS.REPORTS.RESOLVE_BTN}
            className="mt2 w-20"
            color="primary"
            onClick={handleResolve}
          >
            {formatMessage({ id: 'common.resolve' })}
          </Button>
        </div>
        <Else />
        <If condition={hasAppliedActions}>
          <div styleName="history">
            <p styleName="history-title">
              {formatMessage({ id: 'reports.history' })}
            </p>
            <ul>{getHistory()}</ul>
          </div>
        </If>
      </If>
    </div>
  );
};

ReportDetails.propTypes = {
  report: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  onResolve: PropTypes.func.isRequired,
};

ReportDetails.defaultProps = {
  report: {},
};

export default compose(
  injectIntl,
  CSSModules(styles, { allowMultiple: true }),
)(ReportDetails);



// WEBPACK FOOTER //
// ./src/components/Communities/Reports/ReportDetails/index.jsx